import "core-js/modules/es.object.keys.js";
import { defineComponent as _defineComponent } from 'vue';
import * as Constants from '@/constants';
import NO_IMAGE from '@/assets/noimage.png';
import { DocumentTypeEnum } from 'wklr-backend-sdk/models';
import { formatQuery, formatYmdHmsWithSlash, isExternalViewer } from '@/utility';
import { generateSearchRoute } from '@/utils/routeUtils';
import { getAvailableViewers, isAccessible } from '@/utils/documentUtils';
import { shouldAppendNewIcon } from '@/utils/documentUtils';
import { useStore } from '@/store/useStore';
import { useTelemetry } from '@/plugins/telemetry';
import Icon from '@/components/renewal/common/icon.vue';
import SiteMenuHistoryMetaInfo from '@/components/renewal/header/site-menu-history-meta-info.vue';
import ViewerLink from '@/components/viewer-link.vue';
export default /*#__PURE__*/_defineComponent({
  __name: 'site-menu-history',
  props: {
    loading: {
      type: Boolean
    },
    histories: null,
    disablePersonalFeature: {
      type: Boolean
    }
  },
  emits: ["click:anchor"],
  setup: function setup(__props, _ref) {
    var emit = _ref.emit;
    var store = useStore();
    var telemetry = useTelemetry();
    var route = useRoute();

    // ================
    // 各履歴の表示用の処理
    // ================
    var hiddenLabelPackIds = [1];
    var generateHistoryRoute = function generateHistoryRoute(query) {
      return generateSearchRoute(query, Constants.Search.option);
    };
    var isTagOnlyQuery = function isTagOnlyQuery(query) {
      return Object.keys(query).length === 1 && query.tagId !== undefined;
    };
    var assertHasDocument = function assertHasDocument(historyItem) {
      return 'document' in historyItem;
    };
    var shouldShowNewIcon = function shouldShowNewIcon(document) {
      if (isAccessible(document)) {
        return shouldAppendNewIcon(document.collectedAt);
      }
      return false;
    };
    var assertIsDocRecord = function assertIsDocRecord(document) {
      return 'type' in document;
    };
    var docRecordType = function docRecordType(document) {
      return assertIsDocRecord(document) ? document.type : undefined;
    };
    var accessible = function accessible(document) {
      return 'docAccessible' in document && document.docAccessible;
    };
    var showShareUrlDialog = function showShareUrlDialog(document) {
      store.commit('setShareUrlDialogDocument', document);
    };
    var purchased = function purchased(document) {
      var _document$purchased;
      if (!('purchased' in document)) {
        return false;
      }
      return (_document$purchased = document.purchased) !== null && _document$purchased !== void 0 ? _document$purchased : false;
    };
    var pathToLatestBook = function pathToLatestBook(document) {
      if (!('latestEditionNaturalId' in document)) return;
      if (document.latestEditionNaturalId === document.id) return;
      return generateSearchRoute({
        keyword: 'urn:isbn:' + document.latestEditionNaturalId
      }, Constants.Search.option);
    };
    var isbnLink = function isbnLink(document) {
      var isbn = '';
      if ('uri' in document) {
        var parts = document.uri.split(':');
        if (parts.length < 3) return null;
        if (parts[2] === '') return null;
        isbn = parts[2];
      } else if ('isbn' in document) {
        isbn = document.isbn;
      } else {
        return null;
      }
      return "https://ja.wikipedia.org/wiki/%E7%89%B9%E5%88%A5:%E6%96%87%E7%8C%AE%E8%B3%87%E6%96%99?isbn=".concat(isbn);
    };
    var documentLabel = function documentLabel(document) {
      // アクセシブルでない場合は確定できないのでラベルを表示しない
      if (!isAccessible(document)) return undefined;

      // メタデータしか持っていない場合はラベルを表示しない
      if (document.isMetadataOnly) return undefined;

      // 法律文は常に Web のみなのでラベルを表示しない
      if (document.type === DocumentTypeEnum.Law) return undefined;
      var viewers = getAvailableViewers(document);
      // 添付のみのなど閲覧できない文献は本文なしとして扱う
      if (viewers.length === 0) return '本文なし';
      if (viewers.length > 1) return undefined;
      switch (viewers[0]) {
        case 'web':
          return 'Webのみ';
        case 'pdf':
          return 'PDFのみ';
      }
    };
    var historyDocumentToIconName = function historyDocumentToIconName(type) {
      switch (type) {
        case DocumentTypeEnum.Book:
          return 'book';
        case DocumentTypeEnum.Law:
          return 'format-file';
        case DocumentTypeEnum.Pubcom:
          return 'pubcom';
        case DocumentTypeEnum.Guideline:
          return 'guideline';
        case DocumentTypeEnum.Pdf:
          return 'pdf-box';
        case DocumentTypeEnum.Extinv:
          return 'search';
        case DocumentTypeEnum.Case:
          return 'case';
        default:
          telemetry.sendErrorTelemetry(new Error("\u4E88\u671F\u305B\u306C DocumentType: ".concat(type, " \u3092\u53D7\u3051\u53D6\u308A\u307E\u3057\u305F")), route);
          return 'book';
        // デフォルト値を返す
      }
    };
    var historyQueryToIconName = function historyQueryToIconName(query) {
      var _query$type;
      // NOTE: 現状はquery.typeが1つのみのようなので、最初の要素を見る
      var queryType = (_query$type = query.type) === null || _query$type === void 0 ? void 0 : _query$type[0];
      switch (queryType) {
        case DocumentTypeEnum.Book:
          return 'keyword';
        case DocumentTypeEnum.Law:
          return 'format-file';
        case DocumentTypeEnum.Pubcom:
          return 'pubcom';
        case DocumentTypeEnum.Guideline:
          return 'guideline';
        default:
          telemetry.sendErrorTelemetry(new Error("\u4E88\u671F\u305B\u306C Search Query: ".concat(queryType, " \u3092\u53D7\u3051\u53D6\u308A\u307E\u3057\u305F")), route);
          return 'keyword';
        // デフォルト値を返す
      }
    };
    var handleAnchorClick = function handleAnchorClick() {
      emit('click:anchor');
    };
    return {
      __sfc: true,
      emit: emit,
      store: store,
      telemetry: telemetry,
      route: route,
      hiddenLabelPackIds: hiddenLabelPackIds,
      generateHistoryRoute: generateHistoryRoute,
      isTagOnlyQuery: isTagOnlyQuery,
      assertHasDocument: assertHasDocument,
      shouldShowNewIcon: shouldShowNewIcon,
      assertIsDocRecord: assertIsDocRecord,
      docRecordType: docRecordType,
      accessible: accessible,
      showShareUrlDialog: showShareUrlDialog,
      purchased: purchased,
      pathToLatestBook: pathToLatestBook,
      isbnLink: isbnLink,
      documentLabel: documentLabel,
      historyDocumentToIconName: historyDocumentToIconName,
      historyQueryToIconName: historyQueryToIconName,
      handleAnchorClick: handleAnchorClick,
      NO_IMAGE: NO_IMAGE,
      formatQuery: formatQuery,
      formatYmdHmsWithSlash: formatYmdHmsWithSlash,
      isExternalViewer: isExternalViewer,
      isAccessible: isAccessible,
      Icon: Icon,
      SiteMenuHistoryMetaInfo: SiteMenuHistoryMetaInfo,
      ViewerLink: ViewerLink
    };
  }
});